import { excelParser } from "@/helpers/excele-parser";

const API = process.env.VUE_APP_SHARED_API || "";
const FRONT = process.env.VUE_APP_FRONT_PREVIEW || "";

export const imageUrl = (path) => (path ? API + path : "");
export const artworkUrl = (slug) => (slug ? `${FRONT}artworks/${slug}` : "");
export const catalogueUrl = (token) => `${FRONT}catalogue/${token}`;

export const formatPrice = (price) =>
  price === null || price === undefined || price === ""
    ? ""
    : "$" + Number(price).toLocaleString("en-US", { maximumFractionDigits: 0 });

// Plain-text value of a column, used by the CSV and the "Label: value" lines.
export const cellText = (artwork, key) => {
  switch (key) {
    case "image":
      return imageUrl(artwork.image);
    case "price":
      return formatPrice(artwork.price);
    case "link":
      return artworkUrl(artwork.link);
    default:
      return artwork[key] ?? "";
  }
};

export const exportCatalogueCsv = (artworks, columns, fileName) => {
  const data = artworks.map((artwork) =>
    Object.fromEntries(columns.map((c) => [c.label, cellText(artwork, c.key)]))
  );
  excelParser().exportDataFromJSON(data, fileName, "csv");
};


// ---- PDF (A4, mm) ----

const PAGE_W = 210;
const PAGE_H = 297;
const MARGIN_X = 12;
const MARGIN_TOP = 14;
const MARGIN_BOTTOM = 16;
const COLS = 3;
const GAP_X = 6;
const GAP_Y = 9;
const COL_W = (PAGE_W - MARGIN_X * 2 - GAP_X * (COLS - 1)) / COLS;
const IMAGE_H = 52;
const MAX_DESCRIPTION_LINES = 8;
const DETAIL_KEYS = ["size", "price", "year", "technique", "type", "size_category", "framed", "availability"];

const lineHeight = (pt) => pt * 0.3528 * 1.3;

// Re-encode as JPEG on a white background: keeps the PDF small (PNGs are the
// heavy ones) and flattens transparency. Stored medium images can be stretched,
// so when the original's ratio is known the image is redrawn at that ratio.
const toJpeg = async (blob, ratio) => {
  const bitmap = await createImageBitmap(blob);
  let width = bitmap.width;
  let height = bitmap.height;
  if (ratio) {
    height = Math.min(bitmap.height, 900);
    width = height * ratio;
  }
  const scale = Math.min(1, 900 / Math.max(width, height));
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(width * scale);
  canvas.height = Math.round(height * scale);
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#fff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  bitmap.close();
  return { data: canvas.toDataURL("image/jpeg", 0.82), width: canvas.width, height: canvas.height };
};

const loadImages = async (artworks, fetchImage, onProgress) => {
  const images = new Map();
  const queue = artworks.filter((a) => a.image);
  const total = queue.length;
  let done = 0;
  onProgress(0, total);

  // Six parallel downloads, like a browser's per-host limit.
  const worker = async () => {
    while (queue.length) {
      const artwork = queue.shift();
      try {
        images.set(artwork.id, await toJpeg(await fetchImage(artwork), artwork.image_ratio));
      } catch (_) {
        // A missing or unreadable image just leaves an empty box.
      }
      onProgress(++done, total);
    }
  };
  await Promise.all(Array.from({ length: 6 }, worker));
  return images;
};

// Draws one card at (x, y), or only measures it when draw is false. Returns its height.
const card = (doc, artwork, keys, columns, image, x, y, draw) => {
  let cy = y;
  const text = (lines, pt, style, color = 30) => {
    doc.setFont("times", style).setFontSize(pt).setTextColor(color);
    if (draw) doc.text(lines, x, cy, { baseline: "top", lineHeightFactor: 1.3 });
    cy += lines.length * lineHeight(pt);
  };

  if (keys.includes("image")) {
    if (draw) {
      doc.setFillColor(246, 246, 244).rect(x, cy, COL_W, IMAGE_H, "F");
      if (image) {
        const ratio = Math.min(COL_W / image.width, IMAGE_H / image.height);
        const w = image.width * ratio;
        const h = image.height * ratio;
        doc.addImage(image.data, "JPEG", x + (COL_W - w) / 2, cy + (IMAGE_H - h) / 2, w, h, undefined, "FAST");
      }
    }
    cy += IMAGE_H + 3;
  }
  if (keys.includes("title") && artwork.title) {
    doc.setFont("times", "bold").setFontSize(10);
    text(doc.splitTextToSize(artwork.title, COL_W), 10, "bold");
  }
  if (keys.includes("artist") && artwork.artist) {
    doc.setFont("times", "italic").setFontSize(8.5);
    text(doc.splitTextToSize(artwork.artist, COL_W), 8.5, "italic", 60);
    cy += 1;
  }
  columns
    .filter((c) => DETAIL_KEYS.includes(c.key))
    .forEach((c) => {
      const value = String(cellText(artwork, c.key));
      if (!value) return;
      doc.setFont("times", "normal").setFontSize(8);
      const labelW = doc.getTextWidth(c.label) + 2;
      const lines = doc.splitTextToSize(value, COL_W - labelW);
      if (draw) {
        doc.setTextColor(120).text(c.label, x, cy, { baseline: "top" });
        doc.setTextColor(30).text(lines, x + labelW, cy, { baseline: "top", lineHeightFactor: 1.3 });
      }
      cy += lines.length * lineHeight(8);
    });
  if (keys.includes("description") && artwork.description) {
    cy += 1.5;
    doc.setFont("times", "normal").setFontSize(7.5);
    let lines = doc.splitTextToSize(artwork.description, COL_W);
    if (lines.length > MAX_DESCRIPTION_LINES) {
      lines = lines.slice(0, MAX_DESCRIPTION_LINES);
      lines[lines.length - 1] = lines[lines.length - 1].replace(/\s*\S*$/, "…");
    }
    text(lines, 7.5, "normal", 70);
  }
  if (keys.includes("link") && artwork.link) {
    cy += 1.5;
    doc.setFont("times", "normal").setFontSize(8).setTextColor(31, 60, 42);
    if (draw) {
      doc.textWithLink("View artwork online", x, cy, { url: artworkUrl(artwork.link), baseline: "top" });
    }
    cy += lineHeight(8);
  }
  return cy - y;
};

/**
 * Builds the catalogue PDF in the browser and downloads it.
 * fetchImage(artwork) must resolve to the image Blob (fetched through the API
 * so CORS allows reading it). onProgress(done, total) reports image loading.
 */
export const downloadCataloguePdf = async ({ artworks, columns, title, fileName, fetchImage, onProgress = () => {} }) => {
  const { jsPDF } = await import("jspdf");
  const keys = columns.map((c) => c.key);
  const images = keys.includes("image") ? await loadImages(artworks, fetchImage, onProgress) : new Map();

  const doc = new jsPDF({ unit: "mm", format: "a4", compress: true });
  doc.setProperties({ title });

  // Header on the first page.
  let y = MARGIN_TOP;
  doc.setFont("times", "normal").setFontSize(20).setTextColor(30).setCharSpace(1.8);
  doc.text("CERES", MARGIN_X, y, { baseline: "top" });
  doc.setCharSpace(0).setFontSize(12).text(title, MARGIN_X, y + 9, { baseline: "top" });
  const date = new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
  doc.setFontSize(8).setTextColor(110);
  doc.text(`${artworks.length} artwork${artworks.length === 1 ? "" : "s"}`, PAGE_W - MARGIN_X, y + 9, { align: "right", baseline: "top" });
  doc.text(date, PAGE_W - MARGIN_X, y + 12.5, { align: "right", baseline: "top" });
  y += 18;
  doc.setDrawColor(30).setLineWidth(0.3).line(MARGIN_X, y, PAGE_W - MARGIN_X, y);
  y += 7;

  for (let i = 0; i < artworks.length; i += COLS) {
    const row = artworks.slice(i, i + COLS);
    const rowH = Math.max(...row.map((a) => card(doc, a, keys, columns, images.get(a.id), 0, 0, false)));
    if (y + rowH > PAGE_H - MARGIN_BOTTOM && y > MARGIN_TOP) {
      doc.addPage();
      y = MARGIN_TOP;
    }
    row.forEach((a, col) => card(doc, a, keys, columns, images.get(a.id), MARGIN_X + col * (COL_W + GAP_X), y, true));
    y += rowH + GAP_Y;
  }

  const pages = doc.getNumberOfPages();
  for (let p = 1; p <= pages; p++) {
    doc.setPage(p);
    doc.setFont("times", "normal").setFontSize(7.5).setTextColor(140);
    doc.text(`${p} / ${pages}`, PAGE_W / 2, PAGE_H - 8, { align: "center" });
  }

  doc.save(`${fileName}.pdf`);
};
